import { BehaviorSubject } from 'rxjs';

import {
  CustomerIdent,
  FileExtension,
  IEnvironment,
  IRequestConfig,
  IResidentS3File,
} from '../../models';
import { IActionState } from '../../utils';

import { AuthTokenService } from '../../infrastructure';

export abstract class FileDownloadService {
  abstract documentDownloadActionState$: BehaviorSubject<IActionState>;
  // Make environment and auth service available to all platform implementations
  constructor(
    protected authTokenService: AuthTokenService,
    protected environment: IEnvironment
  ) {}

  protected getExtensionFromMimeType(mimeType: string | string[]): FileExtension {
    if (!mimeType) return null;
    const mimeTypeValue = Array.isArray(mimeType) ? mimeType[0] : mimeType;
    const mimeToExtension: { [key: string]: string } = {
      'image/png': 'png',
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'application/pdf': 'pdf',
      'text/plain': 'txt',
      'application/json': 'json',
      'application/xml': 'xml',
      'application/zip': 'zip',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
    };

    return mimeToExtension[mimeTypeValue.toLowerCase()] as FileExtension;
  }

  protected createRequestForDocument(
    documentId: string,
    customerIdent: CustomerIdent
  ): IRequestConfig {
    const url = this.environment.filerUrl + `documentManagement/download` + `/${documentId}`;
    return {
      url: url,
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + this.authTokenService.getToken().accessToken,
        'X-Customer-Ident': customerIdent,
      },
    };
  }

  protected createRequestForAttachment(
    ticketId: string,
    attachmentIdentifier: string,
    customerIdent: CustomerIdent
  ): IRequestConfig {
    const url = this.environment.filerUrl + `download/ticketAttachment`;
    const body = {
      ticketId,
      attachmentIdentifier,
    };

    return {
      url: url,
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + this.authTokenService.getToken().accessToken,
        'X-Customer-Ident': customerIdent,
        'Content-Type': 'application/json',
      },
      content: JSON.stringify(body),
    };
  }

  protected createRequestForContentTitleImage(
    contentId: string,
    customerIdent: CustomerIdent
  ): IRequestConfig {
    const url = this.environment.filerUrl + `download/contents/${contentId}/titleImage`;

    return {
      url: url,
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + this.authTokenService.getToken().accessToken,
        'X-Customer-Ident': customerIdent,
      },
    };
  }

  protected createRequestForContentAttachment(
    contentId: string,
    attachmentId: string,
    customerIdent: CustomerIdent
  ): IRequestConfig {
    const url =
      this.environment.filerUrl + `download/contents/${contentId}/attachments/${attachmentId}`;

    return {
      url: url,
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + this.authTokenService.getToken().accessToken,
        'X-Customer-Ident': customerIdent,
      },
    };
  }

  abstract downloadDocument(documentId: string);
  abstract downloadFile(residentS3File: IResidentS3File);
  abstract downloadTicketAttachment(ticketId: string, identifier: string, extension?: string);
  /**
   * Resolves the title image of a content entity to a platform specific, displayable source.
   * `titleImageId` is not part of the url — filer-rs keys the title image by content id — but it
   * is part of the cache key so that a replaced title image is not served from a stale cache.
   */
  abstract getContentTitleImage(contentId: string, titleImageId: string);
  abstract downloadContentAttachment(contentId: string, attachmentId: string, extension?: string);
}
